import { useMemo, useState } from "react";
import {
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  Circle,
  InfoWindow,
  Map as GoogleMap,
  Pin,
  Polyline,
} from "@vis.gl/react-google-maps";

import { formatTimeIn } from "@/lib/datetime";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";
import { FitBounds } from "@/components/maps/FitBounds";
import { GoogleMapsGate } from "@/components/maps/GoogleMapsGate";
import { GOOGLE_MAPS_MAP_ID, primaryColor } from "@/lib/google-maps";
import { padForRadius, type Point } from "@/lib/map-geometry";
import type {
  AttendanceHomeDetail,
  AttendanceSessionItem,
  AttendanceSiteDetail,
  GeofenceStatus,
} from "@/lib/attendance-api";

// Home zone theme — a distinct blue so an admin can tell a WFH check-in's
// home geofence apart from the site geofence (primary tone).
const HOME_COLOR = "#0ea5e9";
const INSIDE_COLOR = "#16a34a";
const OUTSIDE_COLOR = "#dc2626";
const NA_COLOR = "#6b7280";
const LINE_COLOR = "#64748b";

/**
 * Read-only attendance map for the admin detail view.
 *
 * Renders:
 *  - The site marker + geofence circle (if the record has a site).
 *  - One numbered check-in pin per session; a matching check-out pin if the
 *    session was closed. Pins are colour-coded by geofence status so admins
 *    can spot an "outside geofence" event at a glance.
 *  - A dashed line from check-in → check-out for each closed session.
 *
 * The map fits itself to every point plus the full geofence circles.
 */

function isValidPoint(lat: number | null, lng: number | null): boolean {
  return lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);
}

function geofenceColor(g: GeofenceStatus | null): string {
  return g === "INSIDE" ? INSIDE_COLOR : g === "OUTSIDE" ? OUTSIDE_COLOR : NA_COLOR;
}

/**
 * Numbered session pin. Ring = check-in, square = check-out, so the two stay
 * distinguishable without relying on colour alone.
 */
function SessionPin({
  label,
  kind,
  geofence,
}: {
  label: string;
  kind: "in" | "out";
  geofence: GeofenceStatus | null;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 28,
        height: 28,
        background: geofenceColor(geofence),
        color: "white",
        border: "2px solid white",
        borderRadius: kind === "in" ? 9999 : 4,
        boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
        font: "600 11px/1 system-ui, sans-serif",
      }}
    >
      {label}
    </div>
  );
}

/** Dashes, since a Google polyline has no dash style of its own. */
const DASHED: google.maps.IconSequence[] = [
  {
    icon: { path: "M 0,-1 0,1", strokeOpacity: 0.7, strokeColor: LINE_COLOR, scale: 2 },
    offset: "0",
    repeat: "8px",
  },
];

/** Session times on the map read in the tenant's timezone, like the table. */
function useFormatTime(): (iso: string | null) => string {
  const tz = useTenantTimezone();
  return (iso) => formatTimeIn(iso, tz);
}

function MapBody({
  site,
  sessions,
  home,
  points,
}: {
  site: AttendanceSiteDetail | null;
  sessions: AttendanceSessionItem[];
  home: AttendanceHomeDetail | null;
  points: Point[];
}) {
  const formatTime = useFormatTime();
  const color = useMemo(primaryColor, []);
  const [info, setInfo] = useState<"site" | "home" | null>(null);

  // Fit to the pins *and* the edges of each geofence, not just their centres.
  const fitPoints = useMemo(() => {
    const out = [...points];
    if (site) out.push(...padForRadius({ lat: site.latitude, lng: site.longitude }, site.geofenceRadiusM));
    if (home) out.push(...padForRadius({ lat: home.latitude, lng: home.longitude }, home.radiusM));
    return out;
  }, [points, site, home]);

  const enforced = site?.geofenceMode === "GPS_AND_GEOFENCE";

  return (
    <GoogleMap
      mapId={GOOGLE_MAPS_MAP_ID}
      defaultCenter={points[0]}
      defaultZoom={17}
      gestureHandling="greedy"
      disableDefaultUI
      zoomControl
      clickableIcons={false}
      style={{ width: "100%", height: "100%" }}
    >
      <FitBounds points={fitPoints} />

      {site && (
        <>
          {/* An unenforced geofence draws fainter: the circle is informational,
              and nobody will be refused for standing outside it. */}
          <Circle
            center={{ lat: site.latitude, lng: site.longitude }}
            radius={site.geofenceRadiusM}
            clickable={false}
            strokeColor={color}
            strokeWeight={enforced ? 2 : 1}
            strokeOpacity={enforced ? 0.9 : 0.45}
            fillColor={color}
            fillOpacity={enforced ? 0.12 : 0.05}
          />
          <AdvancedMarker
            position={{ lat: site.latitude, lng: site.longitude }}
            title={site.name}
            onClick={() => setInfo("site")}
          >
            <Pin background={color} borderColor="#ffffff" glyphColor="#ffffff" />
          </AdvancedMarker>
          {info === "site" && (
            <InfoWindow
              position={{ lat: site.latitude, lng: site.longitude }}
              pixelOffset={[0, -36]}
              onCloseClick={() => setInfo(null)}
            >
              <div className="text-xs text-slate-900">
                <div className="font-semibold">{site.name}</div>
                {site.address && <div className="mt-0.5 text-slate-600">{site.address}</div>}
                <div className="mt-1 font-mono text-[10px] text-slate-600">
                  {site.latitude.toFixed(5)}, {site.longitude.toFixed(5)}
                </div>
                <div className="mt-1 text-[10px]">
                  {site.geofenceMode === "GPS_AND_GEOFENCE"
                    ? `Geofence ${site.geofenceRadiusM}m`
                    : site.geofenceMode === "GPS_ONLY"
                      ? "GPS only (no enforcement)"
                      : "Manual mode"}
                </div>
              </div>
            </InfoWindow>
          )}
        </>
      )}

      {home && (
        <>
          <Circle
            center={{ lat: home.latitude, lng: home.longitude }}
            radius={home.radiusM}
            clickable={false}
            strokeColor={HOME_COLOR}
            strokeWeight={2}
            strokeOpacity={0.8}
            fillColor={HOME_COLOR}
            fillOpacity={0.1}
          />
          <AdvancedMarker
            position={{ lat: home.latitude, lng: home.longitude }}
            anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
            title="Home location"
            onClick={() => setInfo("home")}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 26,
                height: 26,
                background: HOME_COLOR,
                color: "white",
                border: "2px solid white",
                borderRadius: 9999,
                boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
                font: "600 13px/1 system-ui, sans-serif",
              }}
            >
              ⌂
            </div>
          </AdvancedMarker>
          {info === "home" && (
            <InfoWindow
              position={{ lat: home.latitude, lng: home.longitude }}
              pixelOffset={[0, -16]}
              onCloseClick={() => setInfo(null)}
            >
              <div className="text-xs text-slate-900">
                <div className="font-semibold">Home location</div>
                <div className="mt-1 font-mono text-[10px] text-slate-600">
                  {home.latitude.toFixed(5)}, {home.longitude.toFixed(5)}
                </div>
                <div className="mt-1 text-[10px]">WFH zone · {home.radiusM}m radius</div>
              </div>
            </InfoWindow>
          )}
        </>
      )}

      {sessions.map((s, i) => {
        const label = String(i + 1);
        const inPoint = isValidPoint(s.startLatitude, s.startLongitude)
          ? { lat: s.startLatitude as number, lng: s.startLongitude as number }
          : null;
        const outPoint = isValidPoint(s.endLatitude, s.endLongitude)
          ? { lat: s.endLatitude as number, lng: s.endLongitude as number }
          : null;
        return (
          <SessionLayer
            key={s.id}
            label={label}
            inPoint={inPoint}
            outPoint={outPoint}
            startTitle={`Check-in #${label} · ${formatTime(s.startAt)}`}
            endTitle={`Check-out #${label} · ${formatTime(s.endAt)}`}
            startGeo={s.startGeofenceStatus}
            endGeo={s.endGeofenceStatus}
          />
        );
      })}
    </GoogleMap>
  );
}

function SessionLayer({
  label,
  inPoint,
  outPoint,
  startTitle,
  endTitle,
  startGeo,
  endGeo,
}: {
  label: string;
  inPoint: Point | null;
  outPoint: Point | null;
  startTitle: string;
  endTitle: string;
  startGeo: GeofenceStatus | null;
  endGeo: GeofenceStatus | null;
}) {
  return (
    <>
      {inPoint && outPoint && (
        <Polyline path={[inPoint, outPoint]} strokeOpacity={0} icons={DASHED} clickable={false} />
      )}
      {inPoint && (
        <AdvancedMarker
          position={inPoint}
          anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
          title={startTitle}
        >
          <SessionPin label={label} kind="in" geofence={startGeo} />
        </AdvancedMarker>
      )}
      {outPoint && (
        <AdvancedMarker
          position={outPoint}
          anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
          title={endTitle}
        >
          <SessionPin label={label} kind="out" geofence={endGeo} />
        </AdvancedMarker>
      )}
    </>
  );
}

export function AttendanceMap({
  site,
  sessions,
  home = null,
}: {
  site: AttendanceSiteDetail | null;
  sessions: AttendanceSessionItem[];
  /** Employee home geofence — HR-tier only; drawn so WFH check-ins can be verified. */
  home?: AttendanceHomeDetail | null;
}) {
  const points = useMemo<Point[]>(() => {
    const list: Point[] = [];
    if (site) list.push({ lat: site.latitude, lng: site.longitude });
    if (home) list.push({ lat: home.latitude, lng: home.longitude });
    for (const s of sessions) {
      if (isValidPoint(s.startLatitude, s.startLongitude)) {
        list.push({ lat: s.startLatitude as number, lng: s.startLongitude as number });
      }
      if (isValidPoint(s.endLatitude, s.endLongitude)) {
        list.push({ lat: s.endLatitude as number, lng: s.endLongitude as number });
      }
    }
    return list;
  }, [site, home, sessions]);

  if (points.length === 0) {
    return (
      <div className="flex h-56 items-center justify-center rounded-lg border border-dashed text-xs text-muted-foreground">
        No GPS data recorded for this day.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <GoogleMapsGate className="h-64">
        <div className="relative h-64 overflow-hidden rounded-lg border">
          <MapBody site={site} sessions={sessions} home={home} points={points} />
        </div>
      </GoogleMapsGate>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
        <LegendDot color={INSIDE_COLOR} shape="circle" label="Inside geofence" />
        <LegendDot color={OUTSIDE_COLOR} shape="circle" label="Outside geofence" />
        <LegendDot color={NA_COLOR} shape="circle" label="No enforcement" />
        {home && <LegendDot color={HOME_COLOR} shape="circle" label="Home (WFH) zone" />}
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-full border-2 border-foreground" />
          Check-in
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 border-2 border-foreground" />
          Check-out
        </span>
      </div>
    </div>
  );
}

function LegendDot({
  color,
  shape,
  label,
}: {
  color: string;
  shape: "circle" | "square";
  label: string;
}) {
  return (
    <span className="flex items-center gap-1">
      <span
        className="inline-block h-3 w-3"
        style={{ background: color, borderRadius: shape === "circle" ? "9999px" : "2px" }}
      />
      {label}
    </span>
  );
}
